// EngagementScoreCard — ranked list of members by composite engagement score

import { useState } from "react";

export interface EngagementMember {
  name: string;
  score: number; // 0–100
  meetingAttendance: number; // 0–100 percentage
  physicalStatus: string;
  losapStatus: string;
  trainingComplete: boolean;
  driverQualified: boolean;
}

export interface EngagementScoreCardProps {
  members: EngagementMember[];
  isLoading?: boolean;
  maxVisible?: number;
}

function getScoreColor(score: number): string {
  if (score > 70) return "text-status-green";
  if (score > 40) return "text-status-amber";
  return "text-status-red";
}

function getBarColor(score: number): string {
  if (score > 70) return "bg-status-green";
  if (score > 40) return "bg-status-amber";
  return "bg-status-red";
}

function getBarBg(score: number): string {
  if (score > 70) return "bg-green-500/15";
  if (score > 40) return "bg-amber-500/15";
  return "bg-red-500/15";
}

// Tiny icon badges for factor breakdown
function FactorBadge({
  label,
  ok,
  title,
}: {
  label: string;
  ok: boolean;
  title: string;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none border ${
        ok
          ? "bg-green-500/10 border-green-500/25 text-green-700 dark:text-green-400"
          : "bg-red-500/10 border-red-500/25 text-red-700 dark:text-red-400"
      }`}
    >
      {label}
    </span>
  );
}

function AttendanceBadge({ value }: { value: number }) {
  const rounded = Math.round(value);
  const ok = value >= 60;
  return (
    <span
      title={`Meeting attendance: ${rounded}%`}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none border ${
        ok
          ? "bg-green-500/10 border-green-500/25 text-green-700 dark:text-green-400"
          : "bg-amber-500/10 border-amber-500/25 text-amber-700 dark:text-amber-400"
      }`}
    >
      {rounded}% mtg
    </span>
  );
}

function MemberRow({
  member,
  rank,
}: {
  member: EngagementMember;
  rank: number;
}) {
  const physOk =
    member.physicalStatus.toLowerCase() !== "expired" &&
    member.physicalStatus.toLowerCase() !== "inactive";
  const losapOk =
    member.losapStatus.toLowerCase() !== "inactive" &&
    member.losapStatus.toLowerCase() !== "ineligible";

  return (
    <div className="flex items-center gap-3 py-2.5 px-1 group">
      {/* Rank */}
      <span className="shrink-0 w-6 text-center text-xs font-bold text-theme-muted tabular-nums">
        {rank}
      </span>

      {/* Name + bar */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-sm font-semibold text-theme-primary truncate">
            {member.name}
          </span>
          <span
            className={`text-sm font-bold tabular-nums shrink-0 ${getScoreColor(member.score)}`}
          >
            {member.score}
          </span>
        </div>

        {/* Score bar */}
        <div
          className={`h-1.5 rounded-full overflow-hidden ${getBarBg(member.score)}`}
        >
          <div
            className={`h-full rounded-full transition-all duration-500 ${getBarColor(member.score)}`}
            style={{ width: `${Math.min(100, Math.max(0, member.score))}%` }}
          />
        </div>

        {/* Factor badges */}
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
          <AttendanceBadge value={member.meetingAttendance} />
          <FactorBadge
            label={`Phys: ${member.physicalStatus}`}
            ok={physOk}
            title={`Physical status: ${member.physicalStatus}`}
          />
          <FactorBadge
            label={`LOSAP: ${member.losapStatus}`}
            ok={losapOk}
            title={`LOSAP status: ${member.losapStatus}`}
          />
          <FactorBadge
            label="Training"
            ok={member.trainingComplete}
            title={`Training: ${member.trainingComplete ? "Complete" : "Incomplete"}`}
          />
          <FactorBadge
            label="Driver"
            ok={member.driverQualified}
            title={`Driver qualified: ${member.driverQualified ? "Yes" : "No"}`}
          />
        </div>
      </div>
    </div>
  );
}

export default function EngagementScoreCard({
  members,
  isLoading = false,
  maxVisible = 20,
}: EngagementScoreCardProps) {
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-3 border-b border-theme-subtle flex items-center justify-between">
          <div className="skeleton h-4 w-40 rounded-md" />
          <div className="skeleton h-5 w-8 rounded-full" />
        </div>
        <div className="p-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="skeleton h-4 w-32 rounded-md" />
                <div className="skeleton h-4 w-8 rounded-md" />
              </div>
              <div className="skeleton h-1.5 rounded-full" />
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, j) => (
                  <div key={j} className="skeleton h-4 w-12 rounded" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Sort by score descending — most engaged first
  const ranked = [...members].sort((a, b) => b.score - a.score);
  const visibleMembers = expanded ? ranked : ranked.slice(0, maxVisible);
  const hasMore = ranked.length > maxVisible;

  // Summary stats
  const avgScore =
    members.length > 0
      ? Math.round(
          members.reduce((sum, m) => sum + m.score, 0) / members.length,
        )
      : 0;
  const highCount = members.filter((m) => m.score > 70).length;

  return (
    <div className="glass rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 border-b border-theme-subtle flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-sm font-semibold text-theme-primary leading-tight">
            Engagement Scores
          </h2>
          {members.length > 0 && (
            <span
              className={`text-xs font-semibold ${getScoreColor(avgScore)}`}
              title={`Avg score: ${avgScore}`}
            >
              avg {avgScore}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {highCount > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-bold bg-green-500/15 text-status-green">
              {highCount} high
            </span>
          )}
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-theme-overlay text-theme-muted">
            {members.length}
          </span>
        </div>
      </div>

      {/* Member list */}
      <div className="px-4 pb-2">
        {members.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm text-theme-muted">
              No member engagement data
            </p>
          </div>
        ) : (
          <>
            <div>
              {visibleMembers.map((member, idx) => (
                <MemberRow key={member.name} member={member} rank={idx + 1} />
              ))}
            </div>

            {/* Expand / collapse */}
            {hasMore && (
              <button
                onClick={() => setExpanded((e) => !e)}
                className="w-full mt-1 text-xs font-semibold text-brand-adaptive hover:text-brand-300 py-1.5 rounded-lg hover:bg-theme-overlay transition-colors"
              >
                {expanded ? "Show less" : `Show all ${members.length} members`}
              </button>
            )}
          </>
        )}
      </div>

      {/* Score legend */}
      <div className="px-5 py-3 border-t border-theme-subtle flex items-center gap-4">
        <span className="text-xs text-theme-muted font-medium">Score:</span>
        {[
          { color: "bg-status-green", label: ">70 High" },
          { color: "bg-status-amber", label: "41–70 Med" },
          { color: "bg-status-red", label: "0–40 At Risk" },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-1.5 rounded-full ${color}`} />
            <span className="text-xs text-theme-muted">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
